import { useCallbackRef } from "../lifecycle";
import {
  paymentTypes,
  updateCheckoutState,
} from "src/utils/redux/slices/checkout";
import {
  createAddress,
  formatAddressProto,
  getAddressRequest,
  getBillingUserRequest,
  updateBillingUserRequest,
} from "src/utils/api/purchase/ordersCheckoutApi";
import { useAppDispatch, useCheckout } from "../redux";
import { setCheckoutCustomerDetails } from "src/utils/redux/slices/checkoutOrders";
import {
  errorConstants,
  ValidationError,
} from "src/utils/services/PurchaseErrorMessages";
import { captureException } from "src/utils/error";
import i18n from "src/locales";
import getStore from "src/utils/redux/store";
import { getActiveUser } from "@noom/hive-auth";
import { requiresPhysicalAddress } from "./physical-address";
import {
  isOrdersPreviewPurchaseCheckout,
  isOrdersCheckout,
  isSelectedMedPlanWithMedication,
  isCrossSellShopCheckout,
} from "src/utils/userSegment/features";
import { getFullNameFromBillingAddress } from "src/utils/name";
import { selectResolvedPhysicalAddressShortRegion } from "src/utils/redux/slices/checkoutPhysicalAddress/address-selectors";
import {
  PaymentMethodProto,
  PaymentMethodProto_Source,
} from "@noom/noom-contracts/noom_contracts/billing/payment_method/payment_method";
import { useOrdersCheckoutMethods } from "./ordersCheckout";
import { useAddonOffers } from "src/hooks/addons/useAddonOffers";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { hasValidBillingAddress } from "src/utils/api/purchase/checkout";
import { hasValidPhysicalAddress } from "src/utils/addressValidation";
import { useSelector } from "react-redux";
import { CustomerDetailsProto } from "@noom/noom-contracts/noom_contracts/billing/bms/common/shared";
import { BillingUserProto } from "@noom/noom-contracts/noom_contracts/billing/user/billing_user";
import { useQuery } from "react-query";
import { queryClient } from "src/utils/fetch";
import { AddressProto } from "@noom/noom-contracts/noom_contracts/billing/address/address";

export const BILLING_USER_QUERY_KEY = "billingUser";
const ADDRESS_QUERY_KEY = "address";

export function getBillingUserQueryKey(
  accessCode: string = getActiveUser()?.accessCode
) {
  return [BILLING_USER_QUERY_KEY, accessCode] as const;
}

function getAddressQueryKey(accessCode?: string, addressId?: string) {
  return [ADDRESS_QUERY_KEY, accessCode, addressId] as const;
}

function setCachedBillingUser(billingUser: BillingUserProto) {
  const accessCode = getActiveUser()?.accessCode;
  if (!accessCode) {
    return;
  }
  queryClient.setQueryData(getBillingUserQueryKey(accessCode), billingUser);
}

function setCachedAddress(address: AddressProto) {
  const accessCode = getActiveUser()?.accessCode;
  if (!accessCode || !address.id) {
    return;
  }
  queryClient.setQueryData(getAddressQueryKey(accessCode, address.id), address);
}

/**
 * Whether the billing user needs their preferredShippingAddressId backfilled
 * from homeAddressId. This applies to branded med users who have a home address
 * on file but never had a preferred shipping address set — which is the common
 * case since branded plans historically didn't require one.
 */
function shouldBackfillPreferredShippingAddress(
  billingUser: BillingUserProto
): boolean {
  return (
    isCrossSellShopCheckout() &&
    !!billingUser.homeAddressId &&
    !billingUser.preferredShippingAddressId
  );
}

/**
 * Copies homeAddressId into preferredShippingAddressId for the billing user.
 * This ensures the cross-sell shop checkout has a valid shipping address to use
 * for the biomarkers kit shipment, even when the user's address is non-editable.
 *
 * Failures are caught and logged — the checkout continues with the original
 * billing user so this never blocks the initialization flow.
 */
async function backfillPreferredShippingAddress(
  accessCode: string,
  billingUser: BillingUserProto
): Promise<BillingUserProto> {
  const task = trackTask("BackfillPreferredShippingAddress", { accessCode });
  task.start();
  try {
    const updated = await updateBillingUserRequest({
      accessCode,
      billingUser: {
        preferredShippingAddressId: billingUser.homeAddressId,
      },
      updateMask: "preferredShippingAddressId",
    });
    task.success();
    return updated ?? billingUser;
  } catch (err) {
    captureException(err, "backfillPreferredShippingAddress");
    task.failed(err);
    return billingUser;
  }
}

export interface BillingUserUpdateOptions {
  paymentToken?: string;
  updatePaymentMethod?: boolean;
  updateShippingAddress?: boolean;
  /**
   * Defaults to true. When false, a failure to recreate the order after the
   * update is not reported to Sentry; the thrown `errorCreateOrder`
   * ValidationError carries it as `cause` for the caller to report.
   */
  reportOrderErrors?: boolean;
}

async function getBillingUserUpdates(
  options: BillingUserUpdateOptions,
  paymentSource: PaymentMethodProto_Source
) {
  const { accessCode } = getActiveUser();
  const { checkout } = getStore().getState();
  const billingName = getFullNameFromBillingAddress(checkout.billingAddress);
  let activePaymentMethod: Partial<PaymentMethodProto> | undefined;
  if (options.updatePaymentMethod && options.paymentToken) {
    const billingAddress = await createAddress({
      accessCode,
      address: checkout.billingAddress,
      recipient: billingName,
    });
    activePaymentMethod = {
      token: options.paymentToken,
      source: paymentSource,
      billingAddressId: billingAddress.id,
    };
  }

  let homeAddressId: string | undefined;
  let preferredShippingAddressId: string | undefined;
  if (options.updateShippingAddress && requiresPhysicalAddress()) {
    const physicalAddress = selectResolvedPhysicalAddressShortRegion(
      getStore().getState()
    );
    const homeAddress = await createAddress({
      accessCode,
      address: physicalAddress,
      recipient: physicalAddress.name ?? billingName,
    });
    setCachedAddress(homeAddress);
    homeAddressId = homeAddress.id;
    preferredShippingAddressId =
      isSelectedMedPlanWithMedication() || isCrossSellShopCheckout()
        ? homeAddressId
        : undefined;
  }

  const updateMask = [
    activePaymentMethod ? "activePaymentMethod" : undefined,
    preferredShippingAddressId ? "preferredShippingAddressId" : undefined,
    homeAddressId ? "homeAddressId" : undefined,
  ]
    .filter(Boolean)
    .join(",");

  return {
    billingUser: {
      activePaymentMethod: activePaymentMethod as PaymentMethodProto,
      preferredShippingAddressId,
      homeAddressId,
    },
    updateMask,
  };
}

export function useUpdateBillingUser() {
  const checkout = useCheckout();
  const dispatch = useAppDispatch();
  const { createOrder } = useOrdersCheckoutMethods();
  const { createAddonOrders } = useAddonOffers({ skipInitialLoad: true });
  const accessCode = getActiveUser()?.accessCode;
  const resolvedPhysicalAddress = useSelector(
    selectResolvedPhysicalAddressShortRegion
  );

  // Used for tax purposes, when we cannot update the billing user, but we want to reflect the address changes in the order tax calculations
  function updateCustomerDetails() {
    if (
      (requiresPhysicalAddress() && hasValidPhysicalAddress()) ||
      (!requiresPhysicalAddress() && hasValidBillingAddress())
    ) {
      const billingName = getFullNameFromBillingAddress(
        checkout.billingAddress
      );
      const customerDetails: CustomerDetailsProto = {
        billingAddress: formatAddressProto(
          checkout.billingAddress,
          billingName
        ),
        shippingAddress: formatAddressProto(
          requiresPhysicalAddress() ? resolvedPhysicalAddress : undefined
        ),
      };
      dispatch(setCheckoutCustomerDetails(customerDetails));
    }
  }

  async function updateBillingUser(options: BillingUserUpdateOptions) {
    // Orders API checkout pages always need real billing user updates.
    const previewOnly =
      isOrdersPreviewPurchaseCheckout() && !isOrdersCheckout();
    const previousProcessingState = getStore().getState().checkout.processing;
    let recreateOrder = false;
    const task = trackTask("UpdateBillingUser", {
      accessCode,
    });
    task.start();
    try {
      dispatch(
        updateCheckoutState({
          processing: {
            isProcessing: true,
            message: previewOnly
              ? undefined
              : i18n.t("processing:updatePaymentInformation"),
          },
          purchaseErrorCode: "",
        })
      );
      if (previewOnly) {
        updateCustomerDetails();
        recreateOrder = true;
      } else {
        const paymentSource =
          checkout.paymentType === paymentTypes.PAYPAL ? "BRAINTREE" : "STRIPE";
        const { billingUser, updateMask } = await getBillingUserUpdates(
          options,
          paymentSource
        );
        const billingUserResponse = await updateBillingUserRequest({
          accessCode,
          billingUser,
          updateMask,
        });
        if (billingUserResponse) {
          setCachedBillingUser(billingUserResponse);
        }
        recreateOrder = !!billingUserResponse;
      }
      task.success();
    } catch (err) {
      captureException(err);
      task.failed(err);
      dispatch(
        updateCheckoutState({
          processing: previousProcessingState,
          showPaymentInfoErrors: true,
        })
      );
      const error = previewOnly
        ? errorConstants.errorUpdateCustomerDetails
        : errorConstants.errorUpdateBillingUser;
      throw new ValidationError([error]);
    }
    const { reportOrderErrors = true } = options;
    try {
      if (recreateOrder) {
        if (isCrossSellShopCheckout()) {
          await createAddonOrders({
            previewMode: false,
            reportErrors: reportOrderErrors,
          });
        } else {
          await createOrder();
        }
      }
    } catch (err) {
      if (reportOrderErrors) {
        captureException(err);
        throw new ValidationError([errorConstants.errorCreateOrder]);
      }
      throw new ValidationError(
        [errorConstants.errorCreateOrder],
        err instanceof Error ? err : undefined
      );
    } finally {
      dispatch(updateCheckoutState({ processing: previousProcessingState }));
    }
  }

  return {
    updateBillingUser: useCallbackRef(updateBillingUser),
  };
}

/**
 * `preferredShippingAddressId` is only written for med-with-medication /
 * cross-sell-shop checkouts. Elsewhere it may hold a stale id from an
 * unrelated order, so only prefer it for those flows.
 */
function resolveShippingAddressId(billingUser?: BillingUserProto) {
  if (isSelectedMedPlanWithMedication() || isCrossSellShopCheckout()) {
    return (
      billingUser?.preferredShippingAddressId ?? billingUser?.homeAddressId
    );
  }
  return billingUser?.homeAddressId;
}

async function fetchActiveBillingUser(accessCode?: string) {
  if (!accessCode) {
    throw new Error("Missing accessCode");
  }
  // The backfill is intentionally inside the query function so that
  // isLoading stays true until the billing user is fully ready
  // (including the preferredShippingAddressId if it needed to be set).
  // The check is idempotent — after the first successful backfill the
  // server already has preferredShippingAddressId, so subsequent fetches
  // skip it.
  const fetched = await getBillingUserRequest(accessCode);
  if (shouldBackfillPreferredShippingAddress(fetched)) {
    return backfillPreferredShippingAddress(accessCode, fetched);
  }
  return fetched;
}

export function useSavedShippingAddress(
  onError: (err: unknown) => void = captureException
): {
  address: AddressProto | null;
  isLoading: boolean;
} {
  const { billingUser, isLoading: isBillingUserLoading } =
    useActiveBillingUser(onError);
  const addressId = resolveShippingAddressId(billingUser);
  const { address, isLoading: isAddressLoading } = useAddress(
    addressId,
    onError
  );
  return { address, isLoading: isAddressLoading || isBillingUserLoading };
}

/**
 * Fetches an address by id.
 */
export function useAddress(
  addressId?: string,
  onError: (err: unknown) => void = captureException
): { address: AddressProto | null; isLoading: boolean } {
  const accessCode = getActiveUser()?.accessCode;

  const { data, isLoading } = useQuery(
    getAddressQueryKey(accessCode, addressId),
    () => getAddressRequest(accessCode, addressId),
    {
      enabled: !!accessCode && !!addressId,
      staleTime: Infinity,
      retry: 2,
      onError,
    }
  );

  return { address: data ?? null, isLoading };
}

/**
 * Hook that fetches the active billing user on mount.
 */
export function useActiveBillingUser(
  onError: (err: unknown) => void = captureException
) {
  const accessCode = getActiveUser()?.accessCode;
  const { data: billingUser, isLoading } = useQuery(
    getBillingUserQueryKey(accessCode),
    () => fetchActiveBillingUser(accessCode),
    {
      enabled: !!accessCode,
      staleTime: Infinity,
      retry: 2,
      onError,
    }
  );

  return {
    billingUser,
    activePaymentMethod: billingUser?.activePaymentMethod,
    billingAddress: billingUser?.activePaymentMethod?.billingAddress,
    isLoading,
  };
}

/**
 * Invalidates the billing user React Query cache. Call after mutating
 * the billing user on the server to keep the cache in sync.
 */
export function invalidateBillingUserQuery() {
  queryClient.invalidateQueries(BILLING_USER_QUERY_KEY);
}
